/**
 * The translation core both apps share: key lookup and `{name}` placeholders
 * over the message trees in `i18n/namespaces/`.
 *
 * Extracted from `lib/i18n-client.tsx` when mobile needed the same thing. It
 * has no React, DOM or cookie in it, so Metro and Vite can both load it, and a
 * string reads the same on the phone as in the browser because it is resolved
 * by the same code.
 */

export type Locale = 'en' | 'de'
export const LOCALES: Locale[] = ['en', 'de']

export type Messages = Record<string, unknown>
export type TranslateValues = Record<string, string | number | Date>

/** A supported locale from any language tag (`"de-DE"` → `"de"`), else null. */
export function toLocale(tag: string | null | undefined): Locale | null {
  const language = tag?.split(/[-_]/)[0]?.toLowerCase()
  return language === 'de' || language === 'en' ? language : null
}

/** Resolve a dot-notation key path inside a nested object. Missing keys return the key. */
export function resolvePath(obj: Record<string, unknown>, key: string): string {
  const parts = key.split('.')
  let current: unknown = obj
  for (const part of parts) {
    if (typeof current !== 'object' || current === null) return key
    current = (current as Record<string, unknown>)[part]
  }
  return typeof current === 'string' ? current : key
}

/** Replace {varName} placeholders with values. */
export function interpolate(str: string, values?: TranslateValues): string {
  if (!values) return str
  return str.replace(/\{(\w+)\}/g, (_, k) => String(values[k] ?? `{${k}}`))
}

/** `t(key, values)` over one namespace of messages. */
export function translate(
  namespace: Record<string, unknown>,
  key: string,
  values?: TranslateValues,
): string {
  return interpolate(resolvePath(namespace, key), values)
}
